import { useState, useEffect, useRef, useCallback } from 'react';
import useDebounce from './useDebounce';

/**
 * Custom hook for search functionality with debouncing and request cancellation
 * @param {Function} searchFunction - The async function to call for searching
 * @param {number} delay - The debounce delay in milliseconds (default: 300ms)
 * @returns {Object} { searchTerm, setSearchTerm, results, loading, error }
 * 
 * @example
 * const { searchTerm, setSearchTerm, results, loading, error } = useSearch(
 *   async (term, signal) => {
 *     const response = await api.searchCustomers(term, { signal });
 *     return response.data;
 *   },
 *   500
 * );
 */
const useSearch = (searchFunction, delay = 300) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const debouncedSearchTerm = useDebounce(searchTerm, delay);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    // Don't search if term is empty (optional - can be customized)
    if (!debouncedSearchTerm && debouncedSearchTerm !== '') {
      setResults(null);
      return;
    }

    const performSearch = async () => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();
      
      setLoading(true);
      setError(null);

      try {
        const result = await searchFunction(debouncedSearchTerm, abortControllerRef.current.signal);
        setResults(result);
      } catch (err) {
        // Don't set error if request was aborted (expected behavior)
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          setError(err);
          console.error('Search error:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    performSearch();

    // Cleanup function to abort request on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedSearchTerm, searchFunction]);

  // Function to reset search
  const reset = useCallback(() => {
    setSearchTerm('');
    setResults(null);
    setError(null);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    results,
    loading,
    error,
    reset
  };
};

export default useSearch;

